import * as React from "react";
import {
    Box,
    Collapse,
    Divider,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
    AccountCircle,
    ExpandLess,
    ExpandMore,
    Logout,
} from "@mui/icons-material";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Authentication from "../modules/authentication";
import { setLoginState, setUserDetails } from "../store/user";
import { sideMenuStructure } from "../routes";

export const SIDE_MENU_WIDTH = 300;

const openedMixin = (theme) => ({
    width: SIDE_MENU_WIDTH,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
});

const closedMixin = (theme) => ({
    width: `calc(${theme.spacing(7)} + 1px)`,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
});

// "Mini variant" drawer - collapses into a narrow rail of icons
const MiniDrawer = styled(Drawer, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    width: SIDE_MENU_WIDTH,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
    }),
}));

/**
 * Expendable side menu item that contains other side menu items
 * @param title title
 * @param icon icon component, shown both when the drawer is expanded and collapsed
 * @param drawerOpen whether the side menu drawer is expanded
 * @param onDrawerOpen called to expand the side menu drawer
 * @param children
 */
function ExpendableSideMenuItem({
    title,
    icon: Icon,
    drawerOpen,
    onDrawerOpen,
    children,
}) {
    const location = useLocation();
    const subMenuItemPaths = React.Children.map(children, (c) => c.props.path);
    const containsCurrentPath = subMenuItemPaths.includes(location.pathname);
    // See if menu should be expanded by default, in case our current URL is equal to the path of
    // its child sub-menu items.
    const [isOpen, setIsOpen] = useState(containsCurrentPath);
    // Render all sub menu items with an additional indentation level
    const newChildren = React.Children.map(children, (c) =>
        React.cloneElement(c, { level: 1 }),
    );

    const onClick = () => {
        if (!drawerOpen) {
            // Collapsed into icon rail - expand the drawer and show this item's sub-menu items
            onDrawerOpen();
            setIsOpen(true);
        } else {
            setIsOpen(!isOpen);
        }
    };

    // An expendable side menu item, containing other side menu items
    return (
        <>
            <Tooltip
                title={drawerOpen ? "" : title}
                placement="right"
                disableInteractive
            >
                <ListItemButton
                    onClick={onClick}
                    selected={!drawerOpen && containsCurrentPath}
                >
                    <ListItemIcon>
                        <Icon />
                    </ListItemIcon>
                    <ListItemText
                        primary={title}
                        sx={{ opacity: drawerOpen ? 1 : 0 }}
                    />
                    {drawerOpen && (isOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
            </Tooltip>
            <Collapse in={isOpen && drawerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {newChildren}
                </List>
            </Collapse>
        </>
    );
}

/**
 * Side menu item
 * @param title title
 * @param path path/URL
 * @param level indentation level (zero = no indentation)
 */
function SideMenuItem({ title, path, level = 0, children = [] }) {
    const location = useLocation();

    // Align sub-menu item text with the text of its parent (past the parent's icon)
    return (
        <ListItemButton
            sx={{ pl: 2 + 7 * level }}
            selected={location.pathname === path}
            component={Link}
            to={path}
        >
            <ListItemText primary={title} />
        </ListItemButton>
    );
}

/**
 * Bottom section of the side menu - logged-in user's email and sign out button
 * @param drawerOpen whether the side menu drawer is expanded
 */
function UserSection({ drawerOpen }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userDetails = useSelector((state) => state.user.userDetails);

    const signOut = () => {
        // Sign out and refresh Redux store
        Authentication.logOut();
        dispatch(setLoginState(Authentication.LOGIN_STATUS_NOT_LOGGED_IN));
        dispatch(setUserDetails(null));
        navigate("/login");
    };

    return (
        <List>
            <Tooltip
                title={drawerOpen || !userDetails ? "" : userDetails.email}
                placement="right"
                disableInteractive
            >
                <ListItem>
                    <ListItemIcon>
                        <AccountCircle />
                    </ListItemIcon>
                    <ListItemText
                        primary={userDetails && userDetails.email}
                        primaryTypographyProps={{ noWrap: true }}
                        sx={{ opacity: drawerOpen ? 1 : 0 }}
                    />
                </ListItem>
            </Tooltip>
            <Tooltip
                title={drawerOpen ? "" : "Sign Out"}
                placement="right"
                disableInteractive
            >
                <ListItemButton onClick={signOut}>
                    <ListItemIcon>
                        <Logout />
                    </ListItemIcon>
                    <ListItemText
                        primary="Sign Out"
                        sx={{ opacity: drawerOpen ? 1 : 0 }}
                    />
                </ListItemButton>
            </Tooltip>
        </List>
    );
}

/**
 * Side menu - renders the side menu of IRIS according to sideMenuStructure,
 * defined in routes.js, with the logged-in user's details at the bottom
 * @param open whether the drawer is expanded (otherwise collapsed into an icon rail)
 * @param onOpen called to expand the drawer
 */
export default function SideMenu({ open, onOpen }) {
    return (
        <MiniDrawer variant="permanent" open={open}>
            <Toolbar />
            <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}>
                <List>
                    {sideMenuStructure.map((parent) => (
                        <ExpendableSideMenuItem
                            key={parent.title}
                            title={parent.title}
                            icon={parent.icon}
                            drawerOpen={open}
                            onDrawerOpen={onOpen}
                        >
                            {parent.children.map((child) => (
                                <SideMenuItem
                                    key={child.title}
                                    title={child.title}
                                    path={child.path}
                                />
                            ))}
                        </ExpendableSideMenuItem>
                    ))}
                </List>
            </Box>
            <Divider />
            <UserSection drawerOpen={open} />
        </MiniDrawer>
    );
}
